import { Form, Input, message } from 'antd';
import React, { useEffect, useState } from 'react';
import FormComponent from 'src/app/components/FormComponent/FormComponent';

import TextArea from 'antd/es/input/TextArea';
import { t } from 'i18next';
import InfoTooltipWithLabel from 'src/app/components/InfoTooltipComponent/InfoTooltipWithLabel';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import { interestImageValidator } from 'src/app/components/UploadInterestImage/upload-interest-image.config';
import UploadInterestImage from 'src/app/components/UploadInterestImage/UploadInterestImage';
import { IFormProps } from 'src/app/forms/form.config';
import { convertVehicleRecordToEditFormData } from 'src/app/forms/VehicleIdentifiedForm/vehicle-identified-form.config';
import { convertToSelectOptionsWithId } from 'src/app/helpers/form.helper';
import {
  useCreateVehicleMutation,
  useGetVehiclesIdentifiedAllQuery,
  useUpdateVehicleMutation,
} from 'src/app/redux/interests/vehicles/vehicleIdentifiedSlice';
import { useGetVehiclesRecognizedQuery } from 'src/app/redux/interests/vehicles/vehicleRecognizedSlice';
import { useGetSeedsQuery } from 'src/app/redux/seeds/seedsSlice';
import { InterestsTypes } from 'src/enums/interests.enum';

const SEARCHABLE_CATEGORIES = ['Car'];

const VehicleIdentifiedForm: React.FC<IFormProps> = ({ form, recordId, destroyModal }) => {
  const selectedCategoryId = Form.useWatch('vehicleTypeId', form);
  const [deletedPictures, setDeletedPictures] = useState<string[]>([]);

  const { data: seeds } = useGetSeedsQuery();

  const { data: vehicleRecognizedList, isLoading } = useGetVehiclesRecognizedQuery(undefined);

  const { data: editData } = useGetVehiclesIdentifiedAllQuery(
    { ids: [recordId!] },
    {
      skip: !recordId,
      selectFromResult: ({ data }) => ({
        data: data?.rows.find((row) => row.id === recordId),
      }),
      refetchOnMountOrArgChange: true,
    },
  );

  const [createVehicle] = useCreateVehicleMutation();
  const [updateVehicle] = useUpdateVehicleMutation();

  const onFinish = (values: any) => {
    if (!recordId) {
      createVehicle(values)
        .unwrap()
        .then(() => {
          message.success(t('success.added'));
          form.resetFields();
          if (destroyModal) destroyModal();
        });
      // .catch((err) => message.error(err?.response?.data?.message || t('errors.unknownError')));
    } else {
      updateVehicle({
        id: recordId,
        ...values,
        deletedPictures: deletedPictures.length ? deletedPictures : undefined,
      })
        .unwrap()
        .then(() => {
          message.success(t('success.edited'));
          form.resetFields();
          if (destroyModal) destroyModal();
        });
      // .catch((err) => message.error(err?.response?.data?.message || t('errors.unknownError')));
    }
  };

  useEffect(() => {
    if (editData) {
      form.setFieldsValue(convertVehicleRecordToEditFormData(editData));
    }
  }, [editData]);

  const selectedCategory = vehicleRecognizedList?.rows?.find((row) => row.id === selectedCategoryId);

  const PictureLabel = (
    <div>
      <InfoTooltipWithLabel
        label={t('forms.vehicleIdentifiedForm.pictures')}
        tooltipContent={
          selectedCategory
            ? t('forms.vehicleIdentifiedForm.tooltips.pictures', {
                searchable: SEARCHABLE_CATEGORIES.includes(selectedCategory?.name ?? '') ? '' : 'not',
              })
            : undefined
        }
      />
    </div>
  );

  if (isLoading) return null;
  return (
    <FormComponent options={{ form, onFinish }}>
      <Form.Item label={t('forms.vehicleIdentifiedForm.name')} name="name" rules={[{ required: true }]}>
        <Input variant="borderless" />
      </Form.Item>

      <Form.Item label={t('forms.vehicleIdentifiedForm.category')} name="vehicleTypeId" rules={[{ required: true }]}>
        <SearchableSelect
          variant="borderless"
          options={convertToSelectOptionsWithId(vehicleRecognizedList?.rows || [])}
        />
      </Form.Item>

      <Form.Item label={t('forms.vehicleIdentifiedForm.brand')} name="brandId" rules={[{ required: true }]}>
        <SearchableSelect variant="borderless" options={convertToSelectOptionsWithId(seeds?.vehicleBrands || [])} />
      </Form.Item>

      <Form.Item label={t('forms.vehicleIdentifiedForm.color')} name="colorId" rules={[{ required: true }]}>
        <SearchableSelect variant="borderless" options={convertToSelectOptionsWithId(seeds?.colors || [])} />
      </Form.Item>

      <Form.Item label={t('forms.vehicleIdentifiedForm.plate')} name="plate" rules={[{ required: true }]}>
        <Input variant="borderless" />
      </Form.Item>

      <Form.Item label={t('forms.vehicleIdentifiedForm.staff')} name="staff">
        <Input variant="borderless" />
      </Form.Item>

      <Form.Item label={t('forms.vehicleIdentifiedForm.organization')} name="organization">
        <Input variant="borderless" />
      </Form.Item>

      <Form.Item label={t('forms.vehicleIdentifiedForm.description')} name="description">
        <TextArea variant="borderless" />
      </Form.Item>

      <Form.Item label={PictureLabel} name="pictures" rules={[{ validator: interestImageValidator }]}>
        <UploadInterestImage
          form={form}
          interestType={InterestsTypes.Vehicle}
          recordId={recordId}
          setDeletedPictures={setDeletedPictures}
        />
      </Form.Item>
    </FormComponent>
  );
};

export default VehicleIdentifiedForm;
